import { collection, doc, addDoc, setDoc, serverTimestamp, writeBatch, deleteField, updateDoc, increment } from "firebase/firestore";
import { db } from "./firebase";
import { stripTreeNode, validateTree, cloneTree, findNodeWithParent, removeNodeById, insertNode, moveNode } from "./firebaseTreeHelpers";

export const createWebsite = async ({
  userId,
  websiteName,
  websiteDescription,
  friendlyResponse,
  websiteDesc,
  messageType, userAction, kairosAction}) => {

  const message = websiteDesc || "No message received";
  try {
    const now = serverTimestamp();

    // 1️⃣ Create website document
    const websiteRef = await addDoc(
      collection(db, "workspaces", userId, "websites"),
      {
        ownerId: userId,
        createdBy: userId,
        status: "active",
        name: websiteName,
        description: websiteDescription,
        friendlyResponse: friendlyResponse || "",
        createdAt: now,
        updatedAt: now,
      }
    );

    const newWebsiteId = websiteRef.id;

    // 2️⃣ Create websiteSettings/general
    await setDoc(
      doc(db, "workspaces", userId, "websites", newWebsiteId, "websiteSettings", "general"),
      {
        name: websiteName,
        description: websiteDescription,
        createdAt: now,
        updatedAt: now,
        updatedBy: userId,
        customDomain: "",
        favicon: "",
        previewDomain: "",
        status: "draft",
        websiteId: newWebsiteId,
        workspaceId: userId,
        firstUserMessage: message,
      }
    );

    // 3️⃣ Create conversation doc INSIDE conversations collection ✅

    await createMessage({ userId: userId, websiteId: newWebsiteId, messageType: messageType, who: "User", message: message, action: userAction});

    await createMessage({ userId: userId, websiteId: newWebsiteId, messageType: messageType, who: "Kairos", message: friendlyResponse, action: kairosAction});

    return { success: true, newWebsiteId };

  } catch (error) {
    console.error("createWebsite error:", error);
    return { success: false, error };
  }
};

export const createMessage = async ({ userId, websiteId, messageType, who, message, action }) => {
  try {
    const payload = {
      messageType: messageType || "general",
      sender: who,
      senderId: who === "Kairos" ? "Kairos" : userId,
      createdBy: who === "Kairos" ? "Kairos" : userId,
      message: message?.trim() || "",
      action: action,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const ref = await addDoc(
      collection(db, "workspaces", userId, "websites", websiteId, "messages"),
      payload
    );
    console.log("Message created:", ref.id);
    return { success: true, messageId: ref.id };
  } catch (error) {
    console.log("Message Create", error);
    return { success: false, error };
  }
};

export const createNewWebsitePage = async ({ userId, websiteId, pageId = "home", pageTitle, html, css }) => {
  try {
    const ref = doc(db, "workspaces", userId, "websites", websiteId, "pages", pageId);

    await setDoc(
      ref,
      {
        pageId,
        pageTitle,
        html,
        css,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(), // fine for now; later you can only set on create
      },
      { merge: true }
    );

    return { success: true, pageId };
  } catch (error) {
    console.error("saveGeneratedPage error:", error);
    return { success: false, error };
  }
};

export const updateWebsitePage = async ({ userId, websiteId, pageId = "home", html, css }) => {
  try {
    const ref = doc(db, "workspaces", userId, "websites", websiteId, "pages", pageId);

    await setDoc(
      ref,
      {
        html,
        css,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    return { success: true };
  } catch (error) {
    console.error("updatePage error:", error);
    return { success: false, error };
  }
};

/**
 * Saves the generated page tree into:
 * workspaces/{userId}/websites/{websiteId}/pages/{pageId}
 */
export async function saveGeneratedPage({
  userId,
  websiteId,
  pageId = "home",
  pageName = "Home",
  pageDescription = "",
  tree, // Page node
}) {
  if (!userId || !websiteId) throw new Error("Missing userId or websiteId");
  if (!tree) throw new Error("Missing tree");

  console.log(tree);

  const ref = doc(db, "workspaces", userId, "websites", websiteId, "pages", pageId);

  try{
    await setDoc(
      ref,
      {
        name: pageName,
        description: pageDescription,
        pageId: pageId,
        rootId: tree.id || "root",
        tree: tree || null,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(), // ok for first-time; later you can switch to merge logic
      },
      { merge: true }
    );
  } catch (error) {
    console.error("saveGeneratedPage error:", error);
    return { success: false, error };
  }
  return { success: true, pageId };
}

const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);

/**
 * Save generated page bundle (Option A)
 * - Page doc stores: tree + pageStyles (AI only)
 * - Components collection stores: props/styles/stack (AI only) + defaultsVersion
 * - Defaults are merged at render-time
 */

export async function saveGeneratedPageBundle({
  userId,
  websiteId,
  pageId,
  page,
  tree,
  components,
  isFirstWrite = false,
}) {
  if (!userId || !websiteId || !pageId) {
    throw new Error("saveGeneratedPageBundle: missing userId/websiteId/pageId");
  }

  const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
  const safeStr = (x) => (typeof x === "string" ? x : "");

  const batch = writeBatch(db);

  const pageRef = doc(db, "workspaces", userId, "websites", websiteId, "pages", pageId);
  const now = serverTimestamp();

  // Page doc (tree + metadata)
  const pagePayload = {
    pageId,
    pageName: page?.props?.name || "Home",
    description: page?.props?.description || "",
    rootId: page?.props?.rootId || "root",
    tree: Array.isArray(tree) ? tree : [],
    updatedAt: now,
    ...(isFirstWrite ? { createdAt: now } : {}),
  };

  batch.set(pageRef, pagePayload, { merge: true });

  // Helpers for default HTML
  const containerTypes = new Set([
    "Page",
    "Header",
    "Section",
    "Div",
    "Row",
    "Column",
    "Grid",
    "Stack",
    "Card",
    "Ul",
    "Li",
    "Footer",
    "NavigationBar",
  ]);

  function defaultHtmlForType({ id, type }) {
    const t = String(type || "Div");
    const marker = `data-k-cmp="${id}"`;

    if (t === "Page") return `<div ${marker} class="k-page">{{children}}</div>`;
    if (t === "Section") return `<section ${marker} class="k-section">{{children}}</section>`;
    if (t === "Row") return `<div ${marker} class="k-row">{{children}}</div>`;
    if (t === "Column") return `<div ${marker} class="k-col">{{children}}</div>`;
    if (t === "Grid") return `<div ${marker} class="k-grid">{{children}}</div>`;
    if (t === "Stack") return `<div ${marker} class="k-stack">{{children}}</div>`;
    if (t === "Card") return `<div ${marker} class="k-card">{{children}}</div>`;
    if (t === "Text") return `<p ${marker} class="k-text">Text</p>`;
    if (t === "Button") return `<a ${marker} class="k-btn" href="#">Click</a>`;
    if (t === "Image") return `<img ${marker} class="k-img" src="" alt="" />`;
    if (t === "Video") return `<div ${marker} class="k-video"></div>`;
    if (t === "Form") return `<form ${marker} class="k-form" method="POST" action="/submit">{{children}}</form>`;
    if (t === "EmailSubscriptionForm")
      return `<form ${marker} class="k-form" method="POST" action="/submit"><input class="k-input" type="email" name="email" placeholder="Email" /><button class="k-btn" type="submit">Subscribe</button></form>`;

    if (containerTypes.has(t)) return `<div ${marker} class="k-${t.toLowerCase()}">{{children}}</div>`;
    return `<div ${marker}></div>`;
  }

  // Components list
  const safeComponents = Array.isArray(components) ? [...components] : [];

  // Ensure Page wrapper exists (id = pageId, typically "home")
  if (!safeComponents.some((c) => String(c?.id) === String(pageId))) {
    safeComponents.unshift({
      id: String(pageId),
      type: "Page",
      html: `<div data-k-cmp="${String(pageId)}" class="k-page">{{children}}</div>`,
      css: "",
    });
  }

  // Ensure root exists (id = rootId)
  const rootId = String(page?.props?.rootId || "root");
  if (!safeComponents.some((c) => String(c?.id) === rootId)) {
    safeComponents.push({
      id: rootId,
      type: "Column",
      html: `<div data-k-cmp="${rootId}" class="k-col">{{children}}</div>`,
      css: "",
    });
  }

  // Write component docs (prefer html/css)
  for (const c of safeComponents) {
    const id = String(c?.id || "");
    const type = String(c?.type || "");
    if (!id || !type) continue;

    const compRef = doc(
      db,
      "workspaces",
      userId,
      "websites",
      websiteId,
      "pages",
      pageId,
      "components",
      id
    );

    const html = safeStr(c?.html) || defaultHtmlForType({ id, type });
    const css = safeStr(c?.css);

    // Back-compat (only if present)
    const props = isObj(c?.props) ? c.props : undefined;
    const styles = isObj(c?.styles) ? c.styles : undefined;
    const stack = isObj(c?.stack) ? c.stack : undefined;

    const compPayload = {
      id,
      type,

      // ✅ NEW
      html,
      css,

      // ✅ optional old fields
      ...(props ? { props } : {}),
      ...(styles ? { styles } : {}),
      ...(stack ? { stack } : {}),

      updatedAt: now,
      ...(isFirstWrite ? { createdAt: now } : {}),
    };

    batch.set(compRef, compPayload, { merge: true });
  }

  await batch.commit();
  return { success: true };
}

function toDotUpdates(prefix, obj) {
  const out = {};
  if (!isObj(obj)) return out;

  for (const [k, v] of Object.entries(obj)) {
    const key = `${prefix}.${k}`;
    // We keep this shallow to avoid deep nesting surprises
    out[key] = v;
  }
  return out;
}

export async function applyComponentUpdates({
  userId,
  websiteId,
  pageId,
  componentUpdates,
}) {
  if (!userId || !websiteId || !pageId) throw new Error("applyComponentUpdates: missing ids");
  if (!Array.isArray(componentUpdates) || componentUpdates.length === 0) return { success: true };

  const batch = writeBatch(db);

  for (const u of componentUpdates) {
    const id = String(u?.id || "");
    if (!id) continue;

    const compRef = doc(
      db,
      "workspaces",
      userId,
      "websites",
      websiteId,
      "pages",
      pageId,
      "components",
      id
    );

    const updates = {
      updatedAt: serverTimestamp(),
    };
    
    // ✅ New HTML/CSS mode (direct set)
    if (typeof u.html === "string") updates.html = u.html;
    if (typeof u.css === "string") updates.css = u.css;
    
    // ✅ Old patch mode still supported
    Object.assign(
      updates,
      toDotUpdates("props", u.propsPatch),
      toDotUpdates("styles", u.stylesPatch),
      toDotUpdates("stack", u.stackPatch)
    );

    // removals
    const removeProps = Array.isArray(u.removeProps) ? u.removeProps : [];
    const removeStyles = Array.isArray(u.removeStyles) ? u.removeStyles : [];
    const removeStack = Array.isArray(u.removeStack) ? u.removeStack : [];

    for (const k of removeProps) updates[`props.${k}`] = deleteField();
    for (const k of removeStyles) updates[`styles.${k}`] = deleteField();
    for (const k of removeStack) updates[`stack.${k}`] = deleteField();

    if (u.removeHtml) updates["html"] = deleteField();
    if (u.removeCss) updates["css"] = deleteField();

    batch.update(compRef, updates);
  }

  await batch.commit();
  return { success: true };
}

export async function applyTreePatch({
  userId,
  websiteId,
  pageId,
  currentTree,
  treePatch,
  componentCreates = [],
  componentDeletes = [],
}) {
  if (!userId || !websiteId || !pageId) throw new Error("applyTreePatch: missing ids");
  if (!treePatch?.op) throw new Error("applyTreePatch: missing op");

  const op = String(treePatch.op);
  const payload = treePatch.payload || {};
  const nextTree = cloneTree(currentTree);

  let finalTree = nextTree;

  if (op === "replaceTree") {
    const incoming = Array.isArray(payload.tree) ? payload.tree.map(stripTreeNode) : [];
    validateTree(incoming);

    // root enforcement
    const root = incoming.find((n) => String(n.id) === "root");
    if (!root) throw new Error('replaceTree: missing root node id="root"');
    if (String(root.type) !== "Column") throw new Error('replaceTree: root.type must be "Column"');

    finalTree = incoming;
  } else if (op === "insertNode") {
    const node = stripTreeNode(payload.node);
    validateTree([node]);
    finalTree = insertNode(finalTree, payload.parentId, payload.index, node);
  } else if (op === "removeNode") {
    finalTree = removeNodeById(finalTree, payload.id);
  } else if (op === "moveNode") {
    finalTree = moveNode(finalTree, payload.id, payload.newParentId, payload.newIndex);
  } else if (op === "updateNodeType") {
    const found = findNodeWithParent(finalTree, payload.id);
    if (!found?.node) throw new Error(`updateNodeType: id not found "${payload.id}"`);
    found.node.type = String(payload.newType);
    validateTree(finalTree);
  } else {
    throw new Error(`applyTreePatch: unsupported op "${op}"`);
  }

  validateTree(finalTree);

  // --- NEW helpers for html/css defaults ---
  const isObj = (x) => x && typeof x === "object" && !Array.isArray(x);
  const safeStr = (x) => (typeof x === "string" ? x : "");

  const containerTypes = new Set([
    "Page",
    "Header",
    "Section",
    "Div",
    "Row",
    "Column",
    "Grid",
    "Stack",
    "Card",
    "Ul",
    "Li",
    "Footer",
    "NavigationBar",
  ]);

  function defaultHtmlForType({ id, type }) {
    const t = String(type || "Div");
    const marker = `data-k-cmp="${id}"`;

    if (t === "Page") return `<div ${marker} class="k-page">{{children}}</div>`;
    if (t === "Section") return `<section ${marker} class="k-section">{{children}}</section>`;
    if (t === "Row") return `<div ${marker} class="k-row">{{children}}</div>`;
    if (t === "Column") return `<div ${marker} class="k-col">{{children}}</div>`;
    if (t === "Grid") return `<div ${marker} class="k-grid">{{children}}</div>`;
    if (t === "Stack") return `<div ${marker} class="k-stack">{{children}}</div>`;
    if (t === "Card") return `<div ${marker} class="k-card">{{children}}</div>`;
    if (t === "Text") return `<p ${marker} class="k-text">Text</p>`;
    if (t === "Button") return `<a ${marker} class="k-btn" href="#">Click</a>`;
    if (t === "Image") return `<img ${marker} class="k-img" src="" alt="" />`;
    if (t === "Video") return `<div ${marker} class="k-video"></div>`;
    if (t === "Form") return `<form ${marker} class="k-form" method="POST" action="/submit">{{children}}</form>`;
    if (t === "EmailSubscriptionForm")
      return `<form ${marker} class="k-form" method="POST" action="/submit"><input class="k-input" type="email" name="email" placeholder="Email" /><button class="k-btn" type="submit">Subscribe</button></form>`;

    // generic container vs leaf
    if (containerTypes.has(t)) return `<div ${marker} class="k-${t.toLowerCase()}">{{children}}</div>`;
    return `<div ${marker}></div>`;
  }

  // ✅ Write batch: update page tree + optional create/delete component docs
  const batch = writeBatch(db);

  const pageRef = doc(db, "workspaces", userId, "websites", websiteId, "pages", pageId);

  batch.set(
    pageRef,
    {
      tree: finalTree,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  // Create new component docs if provided
  for (const c of componentCreates || []) {
    const id = String(c?.id || "");
    const type = String(c?.type || "");
    if (!id || !type) continue;

    const compRef = doc(
      db,
      "workspaces",
      userId,
      "websites",
      websiteId,
      "pages",
      pageId,
      "components",
      id
    );

    const html = safeStr(c?.html) || defaultHtmlForType({ id, type });
    const css = safeStr(c?.css);

    // Back-compat fields (optional)
    const props = isObj(c?.props) ? c.props : undefined;
    const styles = isObj(c?.styles) ? c.styles : undefined;
    const stack = isObj(c?.stack) ? c.stack : undefined;

    batch.set(
      compRef,
      {
        id,
        type,

        // ✅ NEW format
        html,
        css,

        // ✅ Back-compat (kept only if provided)
        ...(props ? { props } : {}),
        ...(styles ? { styles } : {}),
        ...(stack ? { stack } : {}),

        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  // Delete component docs if provided
  for (const idRaw of componentDeletes || []) {
    const id = String(idRaw || "");
    if (!id) continue;

    const compRef = doc(
      db,
      "workspaces",
      userId,
      "websites",
      websiteId,
      "pages",
      pageId,
      "components",
      id
    );

    batch.delete(compRef);
  }

  await batch.commit();
  return { success: true, tree: finalTree };
}













export async function savePageHtmlCss({
  userId,
  websiteId,
  pageId = "home",
  html,
  css,
  isFirstWrite = false,
}) {
  if (!userId || !websiteId || !pageId) throw new Error("savePageHtmlCss: missing ids");

  const pageRef = doc(db, "workspaces", userId, "websites", websiteId, "pages", pageId);

  await setDoc(
    pageRef,
    {
      pageId,
      html: typeof html === "string" ? html : "",
      css: typeof css === "string" ? css : "",
      updatedAt: serverTimestamp(),
      ...(isFirstWrite ? { createdAt: serverTimestamp() } : {}),
    },
    { merge: true }
  );

  return { success: true };
}


export async function creditsDeduction({
  userId,
  websiteId,
  userMessage,
  model = "",
  action = "AI_CALL",
  inputTokens = 0,
  outputTokens = 0,
  totalTokens = 0,
}) {
  if (!userId) throw new Error("creditsDeduction: missing userId");
  if (!websiteId) throw new Error("creditsDeduction: missing websiteId");

  const inTok = Number(inputTokens || 0);
  const outTok = Number(outputTokens || 0);
  const totTok = Number(totalTokens || (inTok + outTok));

  // ✅ exact decimal credits (no rounding)
  const creditsUsed = totTok / 1000;

  // 1) Deduct user credits (users collection outside workspaces)
  const userRef = doc(db, "users", userId);
  await updateDoc(userRef, {
    credits: increment(-creditsUsed),
    updatedAt: serverTimestamp(),
  });

  // 2) Log usage under the website
  const usageColRef = collection(db, "workspaces", userId, "websites", websiteId, "aiUsage");
  const usageRef = await addDoc(usageColRef, {
    userMessage: userMessage,
    model: model,
    action: action,
    inputTokens: inTok,
    outputTokens: outTok,
    totalTokens: totTok,
    creditsUsed,
    createdAt: serverTimestamp(),
  });

  return { success: true, creditsUsed, usageId: usageRef.id };
}